import { InteractionRequiredAuthError } from "@azure/msal-browser";
import{RELOAD_PAGE_EXCEUTION_DURATION,RELOAD_PAGE_EXCEUTION_COUNT} from './AppConstants';
import { msalInstance } from "authConfig";

export const UserAccessToken = async (scope, msalInst) => {
  try {
    const activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();

    const request = {
      scopes: [scope],
      account: activeAccount || accounts[0]
    };

    let authResult = '';
    await msalInstance.acquireTokenSilent(request).then((response) => {
      authResult = response.accessToken;
    }).catch(
      async (error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // fallback to interaction when silent call fails
          return await msalInstance.acquireTokenRedirect(request);
        } else {
          // Handle other errors that are not InteractionRequired
          console.error('Error in acquireTokenSilent:', error);
          throw error; // Re-throw to be caught by main catch block
        }
      });

    return authResult;
  }
  catch (error) {
    console.log('Catch an error: ', error);
    // Let global error handlers deal with authority errors
    throw error; // Re-throw so global handlers can catch it
  }
};

export const getUserName = () => {
  try {
    const activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();
    let userAccount = activeAccount || accounts[0];

   return userAccount.name;
  }
  catch (e) {
    console.log('Erron while accessing the Username from Cookies. ', e)
  }
}

export const getUserEmail = () => {
  try {
    const activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();
   
    let userAccount = activeAccount || accounts[0];
    return userAccount.idTokenClaims.email;
  }
  catch (e) {
    console.log('Erron while accessing the Username from Cookies. ', e)
  }
}

/**
 * Retrieves the current user's ID token from MSAL for logout hint
 * @returns {string|null} - ID token or null if not available
 */
const getIdTokenHint = () => {
  try {
    const activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();
    const userAccount = activeAccount || accounts[0];
    
    if (!userAccount) {
      console.warn('⚠️ No active account found for ID token hint');
      return null;
    }
    
    // Get ID token from the account's idToken property
    const idToken = userAccount.idToken;
    
    if (!idToken) {
      console.warn('⚠️ No ID token found in active account');
      return null;
    }
    
    console.log('✅ ID token hint retrieved successfully');
    return idToken;
    
  } catch (error) {
    console.error('❌ Error retrieving ID token hint:', error);
    return null;
  }
};

/**
 * Application Logout Handler with ID Token Hint Support
 * 
 * Enhanced custom logout implementation that includes id_token_hint parameter
 * required when EnforceIdTokenHintOnLogout="true" in B2C custom policy.
 * 
 * Custom logout implementation that resolves intermittent navigation cancellation issues from MSAL's built-in logout methods.
 * Previously used logoutRedirect() but the redirect to logout endpoint was getting cancelled when reaching
 * the endpoint URL took time due to any reason (network latency, server response time, DNS resolution, etc.).
 * This was an intermittent issue that could occur regardless of network speed when logout URL redirection was delayed.
 * Current approach: Extract config → Get ID token hint → Clear local state → Navigate programmatically to B2C logout.
 * This ensures reliable navigation and application-specific logout without affecting other apps.
 */
export const appLogOutHandler = async () => {
  // Prevent double execution
  // Note: window.logoutInProgress persists across session clearing but gets reset on page navigation/reload
  if (window.logoutInProgress) {
    console.log('⚠️ Logout already in progress, skipping duplicate call');
    return;
  }
  
  window.logoutInProgress = true;
  console.log('🚀 === DIRECT B2C LOGOUT WITH ID TOKEN HINT START ===');
  
  try {
    // Get B2C configuration from MSAL instance BEFORE clearing anything
    const msalConfig = msalInstance.getConfiguration();
    const b2cAuthority = msalConfig.auth.authority;
    const postLogoutRedirectUri = msalConfig.auth.postLogoutRedirectUri || msalConfig.auth.redirectUri;
    
    console.log(`🔧 MSAL Authority: ${b2cAuthority}`);
    console.log(`🔧 MSAL Post-logout redirect: ${postLogoutRedirectUri}`);
    
    // Get ID token hint BEFORE clearing MSAL state (CRITICAL for EnforceIdTokenHintOnLogout)
    const idTokenHint = getIdTokenHint();
    
    if (!idTokenHint) {
      console.error('❌ ID token hint is required but not available - B2C policy enforces ID token hint on logout');
      throw new Error('ID token hint required for logout but not found. Cannot proceed with logout.');
    }
    
    console.log('✅ ID token hint obtained for logout (required by B2C policy)');
    
    // Extract tenant and policy from authority BEFORE clearing
    const authorityParts = b2cAuthority.split('/');
    const tenantDomain = authorityParts[2]; // beckmancoulterdxs.b2clogin.com
    const tenantName = authorityParts[3]; // beckmancoulterdxs.onmicrosoft.com
    const policyName = authorityParts[4]; // B2C_1A_SMART_HRD_SUSI
    
    // Construct B2C logout endpoint URL with ID token hint (REQUIRED)
    const logoutUrl = `https://${tenantDomain}/${tenantName}/${policyName}/oauth2/v2.0/logout?` +
      `post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}&` +
      `id_token_hint=${encodeURIComponent(idTokenHint)}`;
    
    console.log(`🔗 B2C Logout URL with ID token hint: ${logoutUrl.substring(0, 120)}...`);
    
    // Clear MSAL accounts and tokens (after getting all needed info)
    msalInstance.setActiveAccount(null);
    localStorage.clear();
    sessionStorage.clear();
    
    console.log('✅ Local storage and MSAL accounts cleared');
    
    // Direct navigation to B2C logout endpoint with ID token hint (bypasses CORS issues)
    console.log('🌐 Navigating directly to B2C logout endpoint with required ID token hint...');
    
    // Navigate to B2C logout - this should clear session and redirect back
    window.location.href = logoutUrl;
    
    // Safeguard: Reset flag after 5 seconds if navigation somehow fails
    setTimeout(() => {
      if (window.logoutInProgress) {
        console.log('⚠️ Logout navigation may have failed, resetting flag');
        window.logoutInProgress = false;
      }
    }, 5000);
    
  } catch (error) {
    console.error('❌ B2C logout with ID token hint failed:', error);
    
    // Reset flag on error
    window.logoutInProgress = false;
    
    // Simple fallback: clear everything and reload
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload(true);
  }
};

export const appRedirect = () => {
  // Prevent double execution
  if (window.logoutInProgress) {
    console.log('⚠️ Logout already in progress via appRedirect, skipping');
    return;
  }
  appLogOutHandler();
}



function getCurrentTimestamp() {
  return new Date().getTime();
}

function setReoladMethodExceutionCount(count,lastExecutionTime) {
 
  localStorage.setItem('reloadPageExecutionCount', JSON.stringify({
    count: count,
    lastExecutionTime: lastExecutionTime
  }));
}

export const reloadPage = () =>{

  if(localStorage.getItem('reloadPageExecutionCount')){

          let executionData = localStorage.getItem('reloadPageExecutionCount');
          executionData = JSON.parse(executionData);
          
          let count = executionData.count;
          let lastExecutionTime = executionData.lastExecutionTime;

               if((getCurrentTimestamp()-lastExecutionTime)<RELOAD_PAGE_EXCEUTION_DURATION && count<=RELOAD_PAGE_EXCEUTION_COUNT)
                  {  
                      setReoladMethodExceutionCount(++count,getCurrentTimestamp())
                      window.location.href = '/';
                  }
                else
                   {
                    if((getCurrentTimestamp()-lastExecutionTime)>RELOAD_PAGE_EXCEUTION_DURATION)
                      localStorage.removeItem('reloadPageExecutionCount');
                    else
                      appRedirect();
                    }
           
    }else{
            setReoladMethodExceutionCount(1,getCurrentTimestamp());
            window.location.href = '/'
         }
  }


export const getPowerBIToken = async() =>{
  const pbiToken = await UserAccessToken("https://analysis.windows.net/powerbi/api/.default");
  return pbiToken;
}

// Global error handler for no_cached_authority_error
// This error occurs because in appLogOutHandler we manually clear localStorage and sessionStorage,
// which removes MSAL's cached authority metadata. If any pending MSAL operations (like token refresh)
// try to access this cleared cache after logout, they throw no_cached_authority_error.
// The handleAuthorityError function detects this specific error and forces a page reload to reset
// the application state and reinitialize MSAL with fresh authority data.
const handleAuthorityError = (error, context = 'unknown') => {
  if (error && error.message && error.message.includes('no_cached_authority_error')) {
    console.error(`❌ No cached authority error detected in ${context} - forcing page reload`);
    console.error('Error details:', error);
    
    // Add a small delay to ensure logging completes
    setTimeout(() => {
      window.location.reload(true);
    }, 100);
    
    return true; // Indicates error was handled
  }
  return false; // Error not handled
};

// Setup global error listeners for unhandled authority errors
if (typeof window !== 'undefined') {
  // Reset logout flag when app loads
  window.logoutInProgress = false;
  
  // Listen for unhandled promise rejections (catches MSAL errors)
  window.addEventListener('unhandledrejection', (event) => {
    if (handleAuthorityError(event.reason, 'unhandledrejection')) {
      event.preventDefault(); // Prevent default error handling
    }
  });
  
  // Listen for general JavaScript errors
  window.addEventListener('error', (event) => {
    if (handleAuthorityError(event.error, 'javascript_error')) {
      event.preventDefault(); // Prevent default error handling
    }
  });
  
  console.log('🛡️ Global authority error handlers setup complete');
}

